import React, { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Button, Input, Select, SkeletonLoader, AttendeeStack, formatSelectOptions } from '../../../components'
import { useMessage, useModalDialog, useAddToCalendar } from '../../../hooks'
import { candidateService } from '../services'
import { appName } from '../appConfig'
import { useCandidateDialog } from '../hooks'
import { icsFromInterview } from '../helpers/ics'
import { MILESTONE_TYPES, CLOSED_MILESTONES, INTERVIEW_MILESTONES, getMilestone } from '../helpers/milestones'
import '../index.css'

const ROUNDS = [1, 2, 3]
const STAGE_ORDER = MILESTONE_TYPES.map(m => m.value)

const formatSlot = (ts) => new Date(ts).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' })

// Everyone actively in the interview process: still open (no closed outcome) and past
// Prospekt, since an uncontacted lead isn't in a process yet. A candidate with a booked
// interview is always included even if their stage says otherwise — the booking is the
// stronger signal, and it's how a candidate parked back at "Träffad" with next week's
// interview still on the calendar stays visible here.
const isInProcess = (c) => !CLOSED_MILESTONES.has(c.current_milestone_type)
  && (c.current_milestone_type !== 'potential' || !!c.next_interview_start_timestamp)

// ---- Filtering + sorting ----------------------------------------------------------
// Hand-rolled, like every other filter bar in this repo (network's NetworkingOverview is
// the reference; there is still no shared FilterBar component). Two of the four filters are
// multi-select unions — "Anna OR Sofia", not two round trips — and their options are derived
// from the loaded rows, so a new owner or stage appears by itself.
//
// `mine` and `missing` are the two questions this view exists to answer and neither is a
// facet of a single field, so they're boolean toggles rather than dropdowns: "which
// interviews am I on" reads across a candidate's rounds, and "who is missing a time" is a
// gap between the stage reached and the bookings that exist.
const MULTI_FILTER_KEYS = ['owner', 'stage']
const TOGGLE_FILTER_KEYS = ['mine', 'missing']
const EMPTY_FILTERS = { search: '', owner: [], stage: [], mine: false, missing: false }

const SORT_OPTIONS = [
  { value: 'next',  label: 'Närmaste intervju' },
  { value: 'name',  label: 'Namn (A–Ö)' },
  { value: 'stage', label: 'Längst fram i processen' },
]

const nextTime = (c) => (c.next_interview_start_timestamp ? new Date(c.next_interview_start_timestamp).getTime() : Infinity)
const byName = (a, b) => (a.candidate_name || '').localeCompare(b.candidate_name || '', 'sv')

// A round is "missing a time" when the candidate has reached it but nothing is booked —
// the state that quietly stalls a process, hence its own toggle.
const roundsMissingTime = (candidate, rounds) => {
  const currentIdx = STAGE_ORDER.indexOf(candidate.current_milestone_type)
  return INTERVIEW_MILESTONES.filter((stage, i) => currentIdx >= STAGE_ORDER.indexOf(stage) && !rounds[i + 1])
}

const applyFilters = (rows, filters, byCandidate) => {
  const search = filters.search.trim().toLowerCase()
  return rows.filter(c => {
    const rounds = byCandidate.get(c.candidate_id) || {}
    const bookings = Object.values(rounds)

    if (search) {
      const haystack = [c.candidate_name, c.candidate_company_name, c.owner_name, ...bookings.map(b => b.interview_location)]
        .filter(Boolean).join(' ').toLowerCase()
      if (!haystack.includes(search)) return false
    }
    if (filters.owner.length && !filters.owner.some(f => f.value === c.owner_user_id)) return false
    if (filters.stage.length && !filters.stage.some(f => f.value === c.current_milestone_type)) return false
    if (filters.mine && !bookings.some(b => b.user_has_signed_up)) return false
    if (filters.missing && roundsMissingTime(c, rounds).length === 0) return false
    return true
  })
}

const sortRows = (rows, sort) => [...rows].sort((a, b) => {
  if (sort === 'name') return byName(a, b)
  if (sort === 'stage') {
    const diff = STAGE_ORDER.indexOf(b.current_milestone_type) - STAGE_ORDER.indexOf(a.current_milestone_type)
    if (diff !== 0) return diff
    return nextTime(a) - nextTime(b) || byName(a, b)
  }
  // 'next' — soonest upcoming interview first; candidates with nothing booked fall last.
  const diff = nextTime(a) - nextTime(b)
  return diff !== 0 ? diff : byName(a, b)
})

const deriveOwnerOptions = (rows) => {
  const byId = new Map()
  rows.forEach(c => { if (c.owner_user_id != null && !byId.has(c.owner_user_id)) byId.set(c.owner_user_id, c.owner_name || 'Okänd') })
  return [...byId.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, 'sv'))
}

// Stage options follow pipeline order, not alphabetical — MILESTONE_TYPES' order *is* the
// pipeline, and a stage dropdown sorted A–Ö reads as noise.
const deriveStageOptions = (rows) => {
  const present = new Set(rows.map(c => c.current_milestone_type))
  return MILESTONE_TYPES.filter(m => present.has(m.value)).map(m => ({ value: m.value, label: m.label }))
}

export const InterviewProcess = () => {
  const { showMessage } = useMessage()
  const { hideModalDialog } = useModalDialog()
  const { showRescheduleInterviewDialog, showCancelInterviewDialog } = useCandidateDialog()
  const { offerAddToCalendar } = useAddToCalendar()

  const [candidates, setCandidates] = useState([])
  const [interviews, setInterviews] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [sort, setSort] = useState('next')

  // Adapter for the shared Input/Select onChange duality plus the multi-select toggle: the
  // shared Select's isMulti mode reports the single option that was clicked, so the consumer
  // maintains its own array (same as NetworkingOverview.js and feedback's Analyze.js).
  const handleFilterChange = (event) => {
    const { name, value } = event.target !== undefined ? event.target : event
    if (MULTI_FILTER_KEYS.includes(name)) {
      setFilters(prev => {
        const current = prev[name]
        const isSelected = current.some(f => f.value === value)
        return { ...prev, [name]: isSelected ? current.filter(f => f.value !== value) : [...current, { value }] }
      })
    } else {
      setFilters(prev => ({ ...prev, [name]: value || '' }))
    }
  }

  const toggleFilter = (key) => setFilters(prev => ({ ...prev, [key]: !prev[key] }))
  const hasActiveFilters = filters.search !== '' || filters.owner.length > 0 || filters.stage.length > 0
    || TOGGLE_FILTER_KEYS.some(k => filters[k])

  useEffect(() => {
    Promise.all([candidateService.getAll(), candidateService.getInterviews({ all: true })])
      .then(([cRes, iRes]) => { setCandidates(cRes.candidates || []); setInterviews(iRes.interviews || []) })
      .catch(err => showMessage(JSON.stringify(err), 'error'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // candidate_id → round → interview. Keyed on the integer id both payloads carry, so the
  // two fetches don't need a second join on the client.
  const byCandidate = useMemo(() => {
    const map = new Map()
    for (const iv of interviews) {
      if (!map.has(iv.candidate_id)) map.set(iv.candidate_id, {})
      map.get(iv.candidate_id)[iv.interview_round] = iv
    }
    return map
  }, [interviews])

  // Everyone in the process, before filtering — the filter dropdowns' options are derived
  // from this set, so narrowing on one facet doesn't empty the others.
  const rows = useMemo(() => candidates.filter(isInProcess), [candidates])
  const visibleRows = useMemo(
    () => sortRows(applyFilters(rows, filters, byCandidate), sort),
    [rows, filters, byCandidate, sort]
  )

  // Signing yourself up (not cancelling) offers an .ics download, the same follow-up marking
  // interest in an event gets from EventListItem — so the gesture ends the same way wherever
  // you make it, here or in the landing page's Activity widget.
  const toggleSignup = async (interview) => {
    try {
      const res = await candidateService.toggleInterviewSignup(interview.candidate_interview_uuid)
      setInterviews(prev => prev.map(iv => iv.candidate_interview_uuid === interview.candidate_interview_uuid
        ? { ...iv, attendees: res.attendees, attendee_count: res.attendee_count, user_has_signed_up: res.user_has_signed_up }
        : iv))
      if (res.user_has_signed_up) offerAddToCalendar(icsFromInterview(interview))
    } catch (err) {
      showMessage(JSON.stringify(err), 'error')
    }
  }

  const reschedule = (candidate, interview) => {
    showRescheduleInterviewDialog({ ...interview, candidate_name: candidate.candidate_name }, async (payload) => {
      await candidateService.updateInterviewRound(candidate.candidate_uuid, interview.interview_round, payload)
        .then(res => {
          hideModalDialog()
          setInterviews(prev => prev.map(iv => iv.candidate_interview_uuid === interview.candidate_interview_uuid
            ? { ...iv, ...res.interview }
            : iv))
          showMessage('Intervjun ombokad')
        })
        .catch(err => { showMessage(JSON.stringify(err), 'error'); throw err })
    })
  }

  // Cancelling drops the booking from local state; the candidate's stage is untouched, so
  // the row stays put and that round simply reads "Ingen tid bokad" again.
  const cancel = (candidate, interview) => {
    showCancelInterviewDialog({ ...interview, candidate_name: candidate.candidate_name }, async () => {
      await candidateService.removeInterviewRound(candidate.candidate_uuid, interview.interview_round)
        .then(() => {
          hideModalDialog()
          setInterviews(prev => prev.filter(iv => iv.candidate_interview_uuid !== interview.candidate_interview_uuid))
          showMessage('Intervjun avbokad')
        })
        .catch(err => { showMessage(JSON.stringify(err), 'error'); throw err })
    })
  }

  if (loading) return <div className="Candidates wide-margin"><SkeletonLoader /></div>

  return (
    <div className="Candidates wide-margin">
      <div className="cd-detail-header">
        <h1 style={{ wordBreak: 'normal' }}>Intervjuprocess</h1>
      </div>

      {rows.length > 0 &&
        <div className="cd-filter-bar">
          <Input
            className="cd-filter-search"
            type="text"
            name="search"
            placeholder="Sök namn, företag, plats..."
            value={filters.search}
            onChange={handleFilterChange}
            isNullable
          />
          <Select
            className="cd-filter-select"
            name="owner"
            value={filters.owner}
            showMultiLabel
            isMulti
            placeholder="Ägare"
            options={formatSelectOptions({ options: deriveOwnerOptions(rows), optionValue: 'value', optionLabel: 'label' })}
            onChange={handleFilterChange}
          />
          <Select
            className="cd-filter-select"
            name="stage"
            value={filters.stage}
            showMultiLabel
            isMulti
            placeholder="Skede"
            options={formatSelectOptions({ options: deriveStageOptions(rows), optionValue: 'value', optionLabel: 'label' })}
            onChange={handleFilterChange}
          />
          <Button
            value="Jag är anmäld"
            className={"button cd-filter-toggle" + (filters.mine ? " cd-filter-toggle-active" : "")}
            onClick={() => toggleFilter('mine')}
          />
          <Button
            value="Saknar tid"
            className={"button cd-filter-toggle" + (filters.missing ? " cd-filter-toggle-active" : "")}
            onClick={() => toggleFilter('missing')}
          />
          <Select
            className="cd-filter-select cd-filter-sort"
            name="sort"
            value={sort}
            placeholder="Sortering"
            options={formatSelectOptions({ options: SORT_OPTIONS, optionValue: 'value', optionLabel: 'label' })}
            onChange={(e) => setSort((e.target !== undefined ? e.target.value : e.value) || 'next')}
          />
          {hasActiveFilters &&
            <Button value="Rensa filter" className="button cd-filter-clear" onClick={() => setFilters(EMPTY_FILTERS)} />
          }
        </div>
      }

      {rows.length > 0 &&
        <p className="cd-filter-count">
          {visibleRows.length === rows.length
            ? `${rows.length} kandidater i process`
            : `${visibleRows.length} av ${rows.length} kandidater`}
        </p>
      }

      {rows.length === 0
        ? <p style={{ color: 'var(--color-dark-grey)' }}>Ingen kandidat är i intervjuprocess just nu.</p>
        : visibleRows.length === 0
        ? <p className="cd-no-matches">Ingen kandidat matchar filtren.</p>
        : (
          <div className="cd-ip-list">
            {visibleRows.map(c => {
              const rounds = byCandidate.get(c.candidate_id) || {}
              const current = getMilestone(c.current_milestone_type)
              const currentIdx = STAGE_ORDER.indexOf(c.current_milestone_type)
              return (
                <div className="cd-ip-row" key={c.candidate_uuid}>

                  <div className="cd-ip-who">
                    <Link className="cd-person-link" to={`/${appName}/${c.candidate_uuid}`}>{c.candidate_name}</Link>
                    <span className={current.pillClassName}>{current.label}</span>
                    <div className="cd-ip-meta">
                      {[c.candidate_company_name, `Ägare: ${c.owner_name}`].filter(Boolean).join(' · ')}
                    </div>
                  </div>

                  <div className="cd-ip-rounds">
                    {ROUNDS.map(round => {
                      const iv = rounds[round]
                      const stage = INTERVIEW_MILESTONES[round - 1]
                      const reached = currentIdx >= STAGE_ORDER.indexOf(stage)
                      return (
                        <div className={"cd-ip-round" + (reached ? " reached" : "") + (iv ? " booked" : "")} key={round}>
                          <div className="cd-ip-round-head">
                            <span className={`cd-timeline-dot cd-timeline-dot-${stage}${reached ? '' : ' cd-ip-dot-todo'}`} />
                            <span className="cd-ip-round-label">Intervju {round}</span>
                          </div>
                          {iv ? (
                            <>
                              <div className="cd-ip-round-slot">
                                {formatSlot(iv.interview_start_timestamp)}
                                <button type="button" className="cd-ip-round-edit" onClick={() => reschedule(c, iv)} title="Boka om">✎</button>
                                <button type="button" className="cd-ip-round-edit cd-ip-round-cancel" onClick={() => cancel(c, iv)} title="Avboka">✕</button>
                              </div>
                              {iv.interview_location && <div className="cd-ip-round-place">{iv.interview_location}</div>}
                              <AttendeeStack
                                users={iv.attendees || []}
                                isParticipant={!!iv.user_has_signed_up}
                                onToggle={() => toggleSignup(iv)}
                                joinTitle="Anmäl mig"
                                leaveTitle="Avanmäl mig"
                                compact
                              />
                            </>
                          ) : (
                            <div className="cd-ip-round-empty">Ingen tid bokad</div>
                          )}
                        </div>
                      )
                    })}
                  </div>

                </div>
              )
            })}
          </div>
        )}
    </div>
  )
}
